import React, { memo, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  SEVERITY_PIN_LABEL,
  SEVERITY_SUMMARY_NOUN,
  SUMMARY_SEVERITY_ORDER,
} from './LintPanel.constants';
import {
  findingsListStyles,
  findingTextWrapStyles,
  getFindingDotStyles,
  getFindingPinStyles,
  getFindingRowStyles,
  getSummaryDotStyles,
  getSummaryStatStyles,
  lintPanelStyles,
  summaryBarStyles,
} from './LintPanel.styles';
import { LintPanelTestIds } from './LintPanel.testIds';

/**
 * Build the summary entries (severity + human count label) for the bar,
 * skipping severities with no findings.
 *
 * @param {Array<{ severity: string }>} findings
 * @returns {Array<{ severity: string, label: string }>}
 */
const buildSummary = findings => {
  const counts = _.countBy(findings, finding => finding.severity);

  return SUMMARY_SEVERITY_ORDER.reduce((acc, severity) => {
    const count = counts[severity] ?? 0;

    if (count === 0) {
      return acc;
    }

    const noun = SEVERITY_SUMMARY_NOUN[severity];
    const label = `${count} ${count === 1 ? noun.one : noun.many}`;

    return [...acc, { severity, label }];
  }, []);
};

/**
 * Static-analysis findings panel rendered beneath the expression editor in both
 * visual and advanced modes. Renders nothing when there are no findings.
 *
 * @param {{ findings: Array<{ id: string, severity: string, code: string, message: string, detail: string }> }} props
 * @returns {JSX.Element|null}
 */
const LintPanel = ({ findings }) => {
  const summary = useMemo(() => buildSummary(findings), [findings]);

  if (_.isEmpty(findings)) {
    return null;
  }

  return (
    <Box data-testid={LintPanelTestIds.ROOT} sx={lintPanelStyles}>
      <Box sx={summaryBarStyles}>
        {summary.map(({ severity, label }) => (
          <Box key={severity} sx={getSummaryStatStyles(severity)}>
            <Box sx={getSummaryDotStyles(severity)} />
            <Typography color="inherit" variant="medium12">
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={findingsListStyles}>
        {findings.map(finding => (
          <Box key={finding.id} sx={getFindingRowStyles(finding.severity)}>
            <Box sx={getFindingDotStyles(finding.severity)} />
            <Box sx={findingTextWrapStyles}>
              <Typography color="text.primary" variant="medium12">
                {finding.message}
              </Typography>
              <Typography color="text.secondary" variant="regular12">
                {finding.detail}
              </Typography>
            </Box>
            <Box sx={getFindingPinStyles(finding.severity)}>
              <Typography color="inherit" variant="bold12">
                {SEVERITY_PIN_LABEL[finding.severity] ?? finding.severity}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

LintPanel.propTypes = {
  findings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      severity: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      detail: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default memo(LintPanel);
